import { debounce } from "lodash";
import { useCallback, useMemo } from "react";

import useGooglePlacesSuggestions, {
  JOB_SEARCH_COVERAGE,
} from "@/components/shared/location-dropdown/use-google-places-suggestions";
import useCurrentLocation from "@/hooks/use-current-location";
import useRecentSearches from "@/hooks/use-recent-searches";

export const NEARBY_VALUE = "__NEARBY__";

export function useLocationDropdown({
  onChange,
  setOpen,
  useStrictAddress,
}: {
  onChange: (val: string) => void;
  setOpen: (val: boolean) => void;
  useStrictAddress: boolean;
}) {
  const { predictions, searchPredictions } = useGooglePlacesSuggestions({
    useStrictAddress: useStrictAddress,
    coverage: JOB_SEARCH_COVERAGE,
  });

  const addSearch = useRecentSearches((s) => s.addSearch);
  const { isLoading: isResolvingLocation, getCurrentLocation } =
    useCurrentLocation();

  const handleSelect = useCallback(
    async (selectedValue: string) => {
      let address: string;

      if (selectedValue === NEARBY_VALUE) {
        const currentAddress = await getCurrentLocation();

        if (!currentAddress) {
          return;
        }

        address = currentAddress;
      } else {
        address = selectedValue;
      }

      addSearch(address);
      onChange(address);
      setOpen(false);
    },
    [addSearch, getCurrentLocation, onChange, setOpen],
  );

  const fetchPredictions = useMemo(
    () => debounce(searchPredictions, 300),
    [searchPredictions],
  );

  return {
    handleSelect,
    fetchPredictions,
    predictions,
    isResolvingLocation,
  };
}
